import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { Send, Mail, MapPin, Phone, Github, Linkedin, Instagram, Facebook } from 'lucide-react';
import Header from '../Header/Header.jsx';
import Footer from '../Footer/Footer.jsx';
import styles from './Contact.module.css';
import { trackEvent } from '../../utils/analytics.js';

const ContactPage = () => {
  const canvasRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    time: ''
  });

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({ name: '', email: '', message: '' });
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('graphic');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 🎨 Canvas animation (unchanged)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 80;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 2 + 1;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(139, 92, 246, 0.6)';
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.update();
        p.draw();
      });
      requestAnimationFrame(animate);
    };

    animate();
  }, []);

  const handleMouseMove = (e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };

  // 🚀 UPDATED SUBMIT FUNCTION
  const handleSubmit = () => {
    const { name, email, message } = formData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const newErrors = {
      name: name.trim() ? '' : 'Name is required.',
      email: !email.trim() ? 'Email is required.' : !emailRegex.test(email) ? 'Enter a valid email.' : '',
      message: message.trim() ? '' : 'Message is required.',
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(e => e)) return;
    setIsSubmitting(true);
    emailjs.send(
      'service_hf48838',
      'template_hgezx6a',
      { name, email, message },
      '6ieGoApH_4dJXWifX'
    ).then(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      setErrors({ name: '', email: '', message: '' });
      trackEvent('generate_lead', { form_name: 'portfolio_contact_form' });
      setTimeout(() => setSubmitted(false), 3000);
    }).catch(() => {
      setIsSubmitting(false);
      alert('Failed to send. Please try again.');
    });
  };

  return (
    <div className={styles.pageWrapper}>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />

      <div className={styles.contactContainer} onMouseMove={handleMouseMove}>
        <canvas ref={canvasRef} className={styles.canvas} />

        <div className={styles.contentWrapper}>
          <div className={styles.grid}>

            {/* LEFT SIDE */}
            <div className={styles.leftSection}>
              <h1 className={styles.title}>Let's Connect</h1>
              <p className={styles.subtitle}>Have a project in mind? Let's create something amazing together.</p>

              <div className={styles.contactInfo}>
                <div className={styles.contactItem}>
                  <div className={styles.iconWrapper}>
                    <Mail className={styles.icon} />
                  </div>
                  <a href="mailto:pavi.123.chandran@gmail.com" className={styles.contactmail} >pavi.123.chandran@gmail.com</a>
                </div>

                <div className={styles.contactItem}>
                  <div className={styles.iconWrapper}>
                    <Phone className={styles.icon} />
                  </div>
                  <a href="https://wa.me/917561847487" className={styles.contactmail} >+91 756 184 7487</a>
                </div>

                <div className={styles.contactItem}>
                  <div className={styles.iconWrapper}>
                    <MapPin className={styles.icon} />
                  </div>
                  <a href="https://maps.google.com/?q=Thrissur,Kerala,India" target="_blank" rel="noreferrer" className={styles.contactValue}>Thrissur, Kerala, India</a>
                </div>
              </div>

              <div className={styles.socialButtons}>
                <a href="https://github.com/pavithra-chandran" target="_blank" rel="noreferrer" className={styles.socialButton}>
                  <Github className={styles.socialIcon} />
                </a>
                <a href="https://www.linkedin.com/in/pavithra-chandran" target="_blank" rel="noreferrer" className={styles.socialButton}>
                  <Linkedin className={styles.socialIcon} />
                </a>
                <a href="https://www.instagram.com" target="_blank" rel="noreferrer" className={styles.socialButton}>
                  <Instagram className={styles.socialIcon} />
                </a>
              </div>
              
            </div>

            {/* RIGHT SIDE FORM */}
            <div className={styles.rightSection}>
              <div className={styles.formCard}>

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className={styles.formInput}
                />
                {errors.name && <p className={styles.errorMsg}>{errors.name}</p>}

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className={styles.formInput}
                />
                {errors.email && <p className={styles.errorMsg}>{errors.email}</p>}

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me about your project..."
                  className={styles.formTextarea}
                />
                {errors.message && <p className={styles.errorMsg}>{errors.message}</p>}

                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={styles.submitButton}
                >
                  {isSubmitting ? "Sending..." : submitted ? "✓ Sent!" : "Send Message"}
                </button>

              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer mode={mode} />
    </div>
  );
};

export default ContactPage;